import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Button,
  InputLabel,
  FormControl,
  Typography,
  CircularProgress,
  Backdrop,
  Grid,
  FormControlLabel,
  Radio,
  RadioGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import axios from "axios";
import toast from "react-hot-toast";
import { axiosConfig } from "../../axios/axiosConfig";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationTable from "./NotificationTable";

const NOTIFICATION_SOUND_LABELS = {
  silent: "no_sound",
  alert: "custom_channel_id",
  profit_booked: "custom_channel_id_2",
  signal_from: "custom_channel_id_3",
  beep: "custom_channel_id_4",
  tni_new_beep: "custom_channel_id_5",
  tni_new_beep_low: "custom_channel_id_6",
};

const OneSignals = () => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [deeplink, setDeeplink] = useState("");
  const [customImageUrl, setCustomImageUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [notificationMode, setNotificationMode] = useState("send_now");
  const [scheduleType, setScheduleType] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [scheduledDay, setScheduledDay] = useState("");
  const [notificationSound, setNotificationSound] = useState(
    NOTIFICATION_SOUND_LABELS.silent,
  );
  const [open, setOpen] = useState(false);
  const [scheduledListLoading, setScheduledListLoading] = useState(false);
  const [scheduledNotifications, setScheduledNotifications] = useState([]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await axiosConfig({
          method: "get",
          url: "/subscription/subscription_plan",
        });
        const addonPlans = res.data?.response?.addondata || [];
        const apiPlans = res.data?.response?.plans || [];
        const allPlans = [
          { id: "Trial", plan_name: "Trial" },
          { id: "annonymous_user", plan_name: "annonymous_user" },
          { id: "expired", plan_name: "Expired" },
          ...apiPlans,
          ...addonPlans,
        ];

        setPlans(allPlans);
      } catch (err) {
        console.error("Failed to fetch plans:", err);
        const errMsg =
          err.response.data?.response || err.message || "Failed to fetch plans";
        toast.error(errMsg);
      }
    };
    fetchPlans();
  }, []);
  const isValidTime = (time) => /^([01]\d|2[0-3]):([0-5]\d)$/.test(time);

  const handleSubmit = async () => {
    if (!title.trim() || !message.trim()) {
      toast.error("Title and Message are required.");
      return;
    }
    if (plans.length > 0 && selectedPlan.length === 0) {
      toast.error("Please select at least one Subscription Plan.");
      return;
    }

    if (notificationMode === "schedule") {
      if (!scheduleType) {
        toast.error("Please select Schedule Type.");
        return;
      }
      if (!isValidTime(scheduledTime)) {
        toast.error("Please enter valid Scheduled Time (HH:mm).");
        return;
      }
      if (scheduleType === "custom" && !scheduledDay) {
        toast.error("Please select Scheduled Day for custom schedule.");
        return;
      }
    }

    const payload = {
      title,
      message,
      deeplink,
      image_url: customImageUrl?.trim() || null,
      subscription_plan_id: selectedPlan,
      notification_mode: notificationMode,
      channel_id: notificationSound,
    };

    if (notificationMode === "schedule") {
      payload.schedule_type = scheduleType;
      payload.scheduled_time = scheduledTime;
      if (scheduleType === "custom") {
        payload.scheduled_day = scheduledDay;
      }
    }
    try {
      setLoading(true);
      const res = await axiosConfig({
        method: "post",
        url: "/tnibroadmin/notification-triggered",
        data: payload,
      });

      toast.success(res?.data?.response);
      setTitle("");
      setMessage("");
      setDeeplink("");
      setSelectedPlan([]);
      setCustomImageUrl("");
      setNotificationMode("send_now");
      setScheduleType("");
      setScheduledTime("");
      setScheduledDay("");
      scheduledNotificationLists();
    } catch (error) {
      const errMsg =
        error.response?.data || error.message || "Failed to send notification";
      alert(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const scheduledNotificationLists = async () => {
    setScheduledListLoading(true);
    try {
      const response = await axiosConfig({
        method: "get",
        url: `/tnibroadmin/notifications/scheduled/list`,
      });
      setScheduledNotifications(response?.data?.data || []);
    } catch (error) {
      const errMsg =
        error.response?.data?.response ||
        error.message ||
        "Failed to fetch scheduled notifications";
      toast.error(errMsg);
    } finally {
      setScheduledListLoading(false);
    }
  };
  useEffect(() => {
    scheduledNotificationLists();
  }, []);

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          px: 3,
          py: 2,
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 4px 20px rgba(26,32,53,0.3)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <NotificationsActiveIcon sx={{ color: "#eef2f7", fontSize: 26 }} />
          <Box>
            <Typography sx={{ color: "#ffffff", fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
              OneSignal Notifications
            </Typography>
            <Typography sx={{ color: "#eef2f7", fontSize: 12 }}>
              Manage & schedule push notifications
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          onClick={handleOpen}
          sx={{
            background: "#016551",
            color: "#fff",
            fontWeight: 600,
            fontSize: 13,
            px: 2.5,
            py: 1,
            borderRadius: "8px",
            textTransform: "none",
            boxShadow: "0 2px 8px rgba(29,185,84,0.4)",
            "&:hover": { background: "linear-gradient(135deg, #17a349 0%, #148040 100%)" },
          }}
        >
          + Send Notification
        </Button>
      </Box>

      {/* Send Notification Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            background: "#04343a",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <NotificationsActiveIcon sx={{ color: "#04343a", fontSize: 20 }} />
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>
              Send Notification
            </Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: "#b39ddb", "&:hover": { color: "#fff" } }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, background: "#f8fafc" }}>
          <Backdrop
            open={loading}
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: "#fff" }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>

          <Box sx={{ width: "100%", maxWidth: 800 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Title"
                  required
                  size="small"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Deeplink (optional)"
                  size="small"
                  value={deeplink}
                  onChange={(e) => setDeeplink(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required size="small">
                  <InputLabel id="plan-select-label">Select Plan(s)</InputLabel>
                  <Select
                    labelId="plan-select-label"
                    multiple
                    value={selectedPlan}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    label="Select Plan(s)"
                    renderValue={(selected) =>
                      plans
                        .filter((plan) => {
                          const planValue =
                            plan.plan_name === "Trial"
                              ? "Trial"
                              : plan.plan_name === "annonymous_user"
                                ? "annonymous_user"
                                : plan.plan_name === "Expired"
                                  ? "expired"
                                  : plan?.data?.[0]?.id;
                          return selected.includes(planValue);
                        })
                        .map((plan) => {
                          if (plan.plan_name === "Trial") return "Trial";
                          if (plan.plan_name === "annonymous_user") return "Annonymous User";
                          if (plan.plan_name === "Expired") return "Expired";
                          return plan?.data?.[0]?.plan_code || "Unknown";
                        })
                        .join(", ")
                    }
                  >
                    {plans.map((plan) => {
                      const planValue =
                        plan.plan_name === "Trial"
                          ? "Trial"
                          : plan.plan_name === "annonymous_user"
                            ? "annonymous_user"
                            : plan.plan_name === "Expired"
                              ? "expired"
                              : plan?.data?.[0]?.id;
                      const planLabel =
                        plan.plan_name === "Trial"
                          ? "Trial"
                          : plan.plan_name === "annonymous_user"
                            ? "Annonymous User"
                            : plan.plan_name === "Expired"
                              ? "Expired"
                              : plan?.data?.[0]?.plan_code || "Unknown";

                      return (
                        <MenuItem key={planValue} value={planValue}>
                          {planLabel}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Your Message"
                  multiline
                  rows={4}
                  required
                  size="small"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Image URL (optional)"
                  placeholder="https://example.com/image.jpg"
                  size="small"
                  value={customImageUrl}
                  onChange={(e) => setCustomImageUrl(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="sound-label">Notification Sound</InputLabel>
                  <Select
                    labelId="sound-label"
                    label="Notification Sound"
                    value={notificationSound}
                    onChange={(e) => setNotificationSound(e.target.value)}
                  >
                    {Object.entries(NOTIFICATION_SOUND_LABELS).map(([key, value]) => (
                      <MenuItem key={key} value={value}>
                        {key}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Typography fontWeight={600} fontSize={13} sx={{ mb: 0.5, color: "#374151" }}>
                  Notification Mode
                </Typography>
                <RadioGroup
                  row
                  value={notificationMode}
                  onChange={(e) => setNotificationMode(e.target.value)}
                >
                  <FormControlLabel
                    value="send_now"
                    control={<Radio size="small" />}
                    label={<Typography fontSize={13}>Send Now</Typography>}
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    value="schedule"
                    control={<Radio size="small" />}
                    label={<Typography fontSize={13}>Schedule</Typography>}
                    sx={{ m: 0 }}
                  />
                </RadioGroup>
              </Grid>

              {notificationMode === "schedule" && (
                <>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="schedule-type-label">Schedule Type</InputLabel>
                      <Select
                        labelId="schedule-type-label"
                        value={scheduleType}
                        label="Schedule Type"
                        onChange={(e) => setScheduleType(e.target.value)}
                      >
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="custom">Custom</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Scheduled Time (HH:mm)"
                      value={scheduledTime}
                      size="small"
                      onChange={(e) => setScheduledTime(e.target.value)}
                      placeholder="e.g., 22:00"
                    />
                  </Grid>
                  {scheduleType === "custom" && (
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="scheduled-day-label">Scheduled Day</InputLabel>
                        <Select
                          labelId="scheduled-day-label"
                          value={scheduledDay}
                          label="Scheduled Day"
                          onChange={(e) => setScheduledDay(e.target.value)}
                        >
                          {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => (
                            <MenuItem key={day} value={day}>{day}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </>
              )}

              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 14,
                    py: 1.2,
                    borderRadius: "8px",
                    textTransform: "none",
                    "&:hover": { background: "linear-gradient(135deg, #17a349 0%, #148040 100%)" },
                  }}
                >
                  {loading ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Send Notification"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Scheduled Notifications Table */}
      <Box
        sx={{
          background: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          mt: 1,
        }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: "1px solid #e2e8f0",
            background: "#04343a",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography sx={{ color: "#e2e8f0", fontWeight: 700, fontSize: 14 }}>
            Scheduled Notifications
          </Typography>
        </Box>
        <NotificationTable
          rows={scheduledNotifications}
          loading={scheduledListLoading}
          planList={plans}
          refreshList={scheduledNotificationLists}
        />
      </Box>
    </Box>
  );
};

export default OneSignals;
